import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { ArrowBendDownRight, CaretRight, Check, DeviceMobile, Minus, Users, X } from '@phosphor-icons/react';

import { ApiError } from '@/api/client';
import {
  listAddressBook,
  startConversation,
  type AddressBookMember,
  type AddressBookTeam,
} from '@/api/chat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { cn } from '@/lib/utils';

// NewChatModal lets an admin/leader start a conversation with anyone in the
// club roster. The roster comes back grouped by team (a searchable accordion):
// expand a team to pick individual members, tick the team header to add the
// whole team at once, or type in the search box to filter across every team.
// Members who haven't linked a login yet are still pickable — they join the
// thread the moment they sign in (see internal/services/identity). Pick one
// person for a direct (1:1) thread, or several — with an optional group name —
// for a group thread. Uses the same native <dialog> pattern as the other
// people-picker modals so focus-trap / escape / backdrop are browser-handled.

export interface NewChatModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  // Called with the new conversation id after a successful create, so the
  // page can open the thread immediately.
  onCreated: (conversationId: string) => void;
}

// teamKey is the stable accordion key for a group: the team id, or a sentinel
// for the synthetic "no team" bucket (team_id is null there).
const NO_TEAM_KEY = '__no_team__';
const keyOf = (t: AddressBookTeam) => t.team_id ?? NO_TEAM_KEY;

// Closed set mirrored from members.member_guardian.relationship. Anything
// outside it (incl. the null/unknown case) falls back to the generic
// "Guardian" badge so the pill is never blank.
const KNOWN_RELATIONSHIPS = [
  'parent',
  'legal_guardian',
  'grandparent',
  'sibling',
  'other_relative',
  'other',
];
function guardianRelationshipLabel(
  relationship: string | undefined,
  t: TFunction<'common'>,
): string {
  if (relationship && KNOWN_RELATIONSHIPS.includes(relationship)) {
    return t(`members.guardians.relationship.${relationship}`);
  }
  return t('chat.new.guardianTag');
}

export function NewChatModal({ open, onClose, slug, onCreated }: NewChatModalProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [search, setSearch] = useState('');
  // selected is a single set of members.member IDs — guardians are members.member
  // rows under the hood, so the participant contract treats them identically.
  // The footer breaks the count down ("X members, Y guardians") for clarity.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Which athlete rows have their guardians revealed (mirrors the Members page
  // chevron-expand pattern).
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [groupTitle, setGroupTitle] = useState('');
  // The team name auto-filled into groupTitle when the picker is exactly the
  // whole-team set of one team. Tracked separately from groupTitle so we can
  // tell "user hasn't typed" (replace freely on team toggle) from "user
  // edited" (leave their text alone).
  const [autoFilledFromTeam, setAutoFilledFromTeam] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // Tracks whether we've seeded the initial expand state for this open cycle.
  const seededRef = useRef(false);

  // Reset state every time the dialog opens so a previous half-finished
  // selection never leaks into a fresh flow.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setSearch('');
      setSelected(new Set());
      setExpanded(new Set());
      setExpandedMembers(new Set());
      setGroupTitle('');
      setAutoFilledFromTeam('');
      setErrorCode(null);
      seededRef.current = false;
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  const addressBookQuery = useQuery({
    queryKey: ['chat', 'address-book', slug],
    queryFn: () => listAddressBook(slug, token),
    enabled: !!token && !!slug && open,
  });

  const teams = useMemo(() => addressBookQuery.data ?? [], [addressBookQuery.data]);

  // Seed the accordion open once data lands: with a handful of groups it's
  // friendlier to show everything expanded; with many, start collapsed so the
  // list stays scannable.
  useEffect(() => {
    if (seededRef.current || teams.length === 0) return;
    seededRef.current = true;
    if (teams.length <= 3) setExpanded(new Set(teams.map(keyOf)));
  }, [teams]);

  // Auto-name the group when the selection is exactly the whole-team set of
  // one team — typical "chat with the A-team" flow. We only overwrite the
  // title if it's empty OR still equal to the previous auto-fill, so a user
  // who types their own name keeps it. Clearing the team selection clears
  // the auto-fill too (but a user-typed title stays).
  useEffect(() => {
    if (teams.length === 0) return;
    let matchedTeam: AddressBookTeam | null = null;
    for (const team of teams) {
      if (!team.team_id || team.members.length === 0) continue;
      if (team.members.length !== selected.size) continue;
      const allPicked = team.members.every((m) => selected.has(m.member_id));
      if (allPicked) {
        matchedTeam = team;
        break;
      }
    }
    const nextName = matchedTeam ? matchedTeam.name : '';
    setGroupTitle((cur) => {
      // Don't clobber a user-typed value.
      if (cur && cur !== autoFilledFromTeam) return cur;
      return nextName;
    });
    setAutoFilledFromTeam(nextName);
  }, [selected, teams, autoFilledFromTeam]);

  // Distinct roster size — counts every pickable row (athletes + their
  // guardians) so the "empty roster" check stays accurate when a team has only
  // pure-guardian rows under it.
  const totalMembers = useMemo(() => {
    const ids = new Set<string>();
    for (const team of teams) {
      for (const m of team.members) {
        ids.add(m.member_id);
        if (m.guardians) for (const g of m.guardians) ids.add(g.member_id);
      }
    }
    return ids.size;
  }, [teams]);

  // While searching we flatten every team into one de-duplicated, sorted list
  // (a member on two teams appears once) so matches are easy to scan. Guardians
  // are included so an admin can find "Anna Andersson" whether she's on the
  // roster directly or only linked as a kid's parent.
  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const searchResults = useMemo(() => {
    if (!searching) return [] as AddressBookMember[];
    const byId = new Map<string, AddressBookMember>();
    const consider = (m: AddressBookMember) => {
      if (byId.has(m.member_id)) return;
      if (m.display_name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)) {
        byId.set(m.member_id, m);
      }
    };
    for (const team of teams) {
      for (const m of team.members) {
        consider(m);
        if (m.guardians) for (const g of m.guardians) consider(g);
      }
    }
    return [...byId.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [teams, q, searching]);

  // Selection breakdown: guardian member-IDs are the union of every guardian
  // across the roster; subtracting from the total selection size gives the
  // member count. A row that appears both as a top-level member and as someone
  // else's guardian counts as a member (top-level takes precedence).
  const { memberSelectedCount, guardianSelectedCount } = useMemo(() => {
    const topLevelIDs = new Set<string>();
    const guardianIDs = new Set<string>();
    for (const team of teams) {
      for (const m of team.members) {
        topLevelIDs.add(m.member_id);
        if (m.guardians) for (const g of m.guardians) guardianIDs.add(g.member_id);
      }
    }
    let mem = 0;
    let gd = 0;
    for (const id of selected) {
      if (topLevelIDs.has(id)) mem++;
      else if (guardianIDs.has(id)) gd++;
      else mem++; // unknown / cached id — count it as a member
    }
    return { memberSelectedCount: mem, guardianSelectedCount: gd };
  }, [selected, teams]);

  const isGroup = selected.size > 1;

  const toggleMember = (memberID: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(memberID)) next.delete(memberID);
      else next.add(memberID);
      return next;
    });

  const toggleTeam = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Reveals or hides a single member's guardian children (the per-row chevron).
  const toggleMemberExpansion = (memberID: string) =>
    setExpandedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(memberID)) next.delete(memberID);
      else next.add(memberID);
      return next;
    });

  // Whole-team selection: "none" | "some" | "all" of the team's members picked.
  const teamSelectionState = (team: AddressBookTeam): 'none' | 'some' | 'all' => {
    if (team.members.length === 0) return 'none';
    let picked = 0;
    for (const m of team.members) if (selected.has(m.member_id)) picked++;
    if (picked === 0) return 'none';
    return picked === team.members.length ? 'all' : 'some';
  };

  const toggleWholeTeam = (team: AddressBookTeam) => {
    const state = teamSelectionState(team);
    setSelected((prev) => {
      const next = new Set(prev);
      // Tick adds everyone in the team; un-tick (when all are picked) clears them.
      if (state === 'all') for (const m of team.members) next.delete(m.member_id);
      else for (const m of team.members) next.add(m.member_id);
      return next;
    });
  };

  const createMut = useMutation({
    mutationFn: () => {
      const ids = [...selected];
      const title = groupTitle.trim();
      return startConversation(
        slug,
        {
          kind: isGroup ? 'group' : 'direct',
          participant_member_ids: ids,
          ...(isGroup && title ? { title } : {}),
        },
        token,
      );
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
      onCreated(res.id);
      onClose();
    },
    onError: (err) => setErrorCode(err instanceof ApiError ? err.code : 'unknown'),
  });

  const canCreate = selected.size > 0 && !createMut.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canCreate) return;
    setErrorCode(null);
    createMut.mutate();
  };

  // depth=0 → top-level athlete/team row, depth=1 → guardian child. The chevron
  // slot mirrors the Members table: a clickable expand caret on parents who
  // have guardians, an angled nesting arrow on the guardian rows themselves.
  // canExpand exposes the per-row caret used to reveal member_guardian children.
  const renderMemberRow = (
    m: AddressBookMember,
    opts: { depth: 0 | 1; canExpand?: boolean; isExpanded?: boolean; onToggleExpand?: () => void },
  ) => {
    const picked = selected.has(m.member_id);
    const { depth, canExpand, isExpanded, onToggleExpand } = opts;
    const isGuardian = depth === 1;
    const secondary = m.email || (m.team_role ? t(`teams.role.${m.team_role}`, { defaultValue: m.team_role }) : '');
    return (
      <div
        key={m.member_id}
        className={cn(
          'flex w-full items-center border-b border-border last:border-b-0 transition-colors',
          picked ? 'bg-primary/10' : 'hover:bg-muted/50',
          'pl-3',
        )}
      >
        {/* Chevron slot: clickable expand caret on parents with guardians, an
            angled nesting arrow on guardian rows (matching the Members table),
            or a fixed-width spacer so checkboxes stay aligned otherwise. */}
        {isGuardian ? (
          <span
            className="flex h-11 w-7 shrink-0 items-center justify-center text-muted-foreground/60"
            aria-hidden
          >
            <ArrowBendDownRight className="h-3.5 w-3.5" />
          </span>
        ) : canExpand ? (
          <button
            type="button"
            onClick={onToggleExpand}
            aria-expanded={!!isExpanded}
            aria-label={t('chat.new.toggleGuardians', { name: m.display_name })}
            className="flex h-11 w-7 shrink-0 items-center justify-center text-muted-foreground"
          >
            <CaretRight
              className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-90')}
              weight="bold"
              aria-hidden
            />
          </button>
        ) : (
          <span className="h-11 w-7 shrink-0" aria-hidden />
        )}
        <button
          type="button"
          onClick={() => toggleMember(m.member_id)}
          aria-pressed={picked}
          className="flex min-h-11 flex-1 items-center gap-3 py-2 pr-3 text-left"
        >
          <span
            aria-hidden
            className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
              picked ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
            )}
          >
            {picked && <Check className="h-3.5 w-3.5" weight="bold" />}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">{m.display_name}</span>
            {secondary && <span className="truncate text-xs text-muted-foreground">{secondary}</span>}
          </span>
          {isGuardian && (
            <span className="shrink-0 rounded-pill bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {guardianRelationshipLabel(m.relationship, t)}
            </span>
          )}
          {m.has_account && (
            <span
              title={t('chat.new.onApp')}
              aria-label={t('chat.new.onApp')}
              className="shrink-0 text-muted-foreground"
            >
              <DeviceMobile className="h-4 w-4" weight="duotone" aria-hidden />
            </span>
          )}
        </button>
      </div>
    );
  };

  // Renders a top-level athlete row plus its expanded guardian children.
  // Used in both browse and search modes — keep behaviour identical so the
  // chevron / guardian rows feel consistent everywhere.
  const renderMemberWithGuardians = (m: AddressBookMember) => {
    const guardians = m.guardians ?? [];
    const isExpanded = expandedMembers.has(m.member_id);
    return (
      <div key={m.member_id}>
        {renderMemberRow(m, {
          depth: 0,
          canExpand: guardians.length > 0,
          isExpanded,
          onToggleExpand: () => toggleMemberExpansion(m.member_id),
        })}
        {isExpanded && guardians.map((g) => renderMemberRow(g, { depth: 1 }))}
      </div>
    );
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <form onSubmit={onSubmit} noValidate className="flex w-[min(36rem,92vw)] flex-col gap-4 p-6">
        <header className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('chat.new.eyebrow')}
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {t('chat.new.title')}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('chat.new.searchLabel')}
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('chat.new.searchPlaceholder')}
            clearLabel={t('common.clearSearch')}
            autoFocus
          />
        </label>

        <div className="max-h-72 overflow-y-auto rounded-xl border border-border">
          {addressBookQuery.isLoading && (
            <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
          )}
          {addressBookQuery.isError && (
            <p role="alert" className="px-3 py-2 text-sm text-destructive">
              {t('chat.new.loadError')}
            </p>
          )}

          {!addressBookQuery.isLoading && !addressBookQuery.isError && totalMembers === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">{t('chat.new.rosterEmpty')}</p>
          )}

          {/* Search mode: flat, de-duplicated list across all teams AND every
              athlete's guardians. Renders without the chevron/nesting — search
              is a global find, not a tree drill-down. */}
          {!addressBookQuery.isLoading && !addressBookQuery.isError && totalMembers > 0 && searching && (
            searchResults.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">{t('chat.new.empty')}</p>
            ) : (
              searchResults.map((m) => renderMemberRow(m, { depth: 0 }))
            )
          )}

          {/* Browse mode: team accordion. */}
          {!addressBookQuery.isLoading &&
            !addressBookQuery.isError &&
            totalMembers > 0 &&
            !searching &&
            teams.map((team) => {
              const key = keyOf(team);
              const isOpen = expanded.has(key);
              const state = teamSelectionState(team);
              const teamName = team.team_id ? team.name : t('chat.new.noTeam');
              return (
                <div key={key} className="border-b border-border last:border-b-0">
                  <div className="flex items-center">
                    {/* Whole-team tick — adds or clears every member in the team. */}
                    <button
                      type="button"
                      onClick={() => toggleWholeTeam(team)}
                      aria-pressed={state === 'all'}
                      aria-label={t('chat.new.selectTeam', { team: teamName })}
                      className="flex h-11 w-11 shrink-0 items-center justify-center"
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'flex h-5 w-5 items-center justify-center rounded-md border',
                          state === 'all'
                            ? 'border-primary bg-primary text-primary-foreground'
                            : state === 'some'
                              ? 'border-primary bg-primary/20 text-primary'
                              : 'border-border',
                        )}
                      >
                        {state === 'all' && <Check className="h-3.5 w-3.5" weight="bold" />}
                        {state === 'some' && <Minus className="h-3.5 w-3.5" weight="bold" />}
                      </span>
                    </button>
                    {/* Expand / collapse the team's members. */}
                    <button
                      type="button"
                      onClick={() => toggleTeam(key)}
                      aria-expanded={isOpen}
                      className="flex min-h-11 flex-1 items-center gap-2 py-2 pr-3 text-left"
                    >
                      <CaretRight
                        className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')}
                        weight="bold"
                        aria-hidden
                      />
                      {team.team_id && <Users className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
                      <span className="truncate text-sm font-semibold text-foreground">{teamName}</span>
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                        {t('chat.new.memberCount', { count: team.members.length })}
                      </span>
                    </button>
                  </div>
                  {isOpen && <div>{team.members.map(renderMemberWithGuardians)}</div>}
                </div>
              );
            })}
        </div>

        {/* Group name only matters once there's more than one participant —
            a 1:1 thread is titled by the other person, not a free-text name. */}
        {isGroup && (
          <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
            {t('chat.new.groupNameLabel')}
            <Input
              value={groupTitle}
              onChange={(e) => setGroupTitle(e.target.value)}
              placeholder={t('chat.new.groupNamePlaceholder')}
              maxLength={120}
            />
          </label>
        )}

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`chat.new.error.${errorCode}`, { defaultValue: t('chat.new.error.unknown') })}
          </p>
        )}

        <footer className="flex items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">
            {selected.size === 0
              ? ' '
              : guardianSelectedCount === 0
                ? t('chat.new.selected', { count: selected.size })
                : `${t('chat.new.selected', { count: selected.size })} · ${t(
                    'chat.new.memberCount',
                    { count: memberSelectedCount },
                  )}, ${t('chat.new.guardianCount', { count: guardianSelectedCount })}`}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={createMut.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canCreate}>
              {createMut.isPending ? t('chat.new.creating') : t('chat.new.create')}
            </Button>
          </div>
        </footer>
      </form>
    </dialog>
  );
}
